import React, { useCallback, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Form, Pagination } from 'react-bootstrap';
import { donationService } from '../services/api';

// Shape of the grid rows as per the provided JSON
export type DonationRecord = {
  app: string;
  entity: 'RMS' | 'HYC' | string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNo: string;
  address: string;
  country: string;
  state: string;
  city: string;
  postalCode: string;
  purposeOfDonation: string | null;
  paymentFor: string;
  donationAmount: number;
  donationType: 'OneTime' | 'Recurring' | string;
  paymentType: 'ACH' | 'Credit Card' | string;
  // allow extra fields from API without breaking
  [k: string]: any;
};

// Pretty-print column headers from JSON keys
const toTitle = (key: string): string => {
  if (!key) return '';
  // special cases
  const specials: Record<string, string> = {
    id: 'ID',
    cvv: 'CVV',
  };
  if (specials[key]) return specials[key];
  // replace underscores with space
  let s = key.replace(/_/g, ' ');
  // add space before capitals in camelCase
  s = s.replace(/([a-z])([A-Z])/g, '$1 $2');
  // collapse multiple spaces
  s = s.replace(/\s+/g, ' ').trim();
  // capitalize words
  s = s
    .split(' ')
    .map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
  return s;
};

const DonationReport: React.FC = () => {
  const [entity, setEntity] = useState<'RMS' | 'HYC'>('RMS');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [rows, setRows] = useState<DonationRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const pageSize = 20;

  // Build dynamic column list from current rows (union of keys)
  const columns = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of rows) {
      Object.keys(r || {}).forEach((k) => set.add(k));
    }
    // provide stable order: some preferred keys first, then the rest sorted
    const preferred = ['app','entity','firstName','lastName','email','phoneNo','address','country','state','city','postalCode','purposeOfDonation','paymentFor','donationAmount','donationType','paymentType'];
    const rest = Array.from(set).filter((k) => !preferred.includes(k));
    rest.sort();
    const ordered = preferred.filter((k) => set.has(k)).concat(rest);
    // Hide app and entity columns per requirements
    return ordered.filter((k) => k !== 'app' && k !== 'entity');
  }, [rows]);

  // Compute a dynamic min-width per column for a slick, compact look
  const getMinWidth = useCallback((col: string): number => {
    switch (col) {
      case 'address':
        return 360;
      case 'email':
        return 220;
      case 'firstName':
      case 'lastName':
      case 'phoneNo':
      case 'country':
      case 'state':
      case 'city':
      case 'postalCode':
      case 'donationType':
      case 'paymentType':
        return 140;
      case 'purposeOfDonation':
        return 240;
      case 'paymentFor':
        return 200;
      case 'donationAmount':
        return 140;
      default:
        return 140; // sensible default for unknown keys
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await donationService.getDonations(entity, fromDate || undefined, toDate || undefined);
      setRows(data as DonationRecord[]);
      setPage(1);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('GetDonations failed', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [entity, fromDate, toDate]);

  // Filter rows by search across all visible columns (case-insensitive)
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      Object.values(r || {}).some((v) => String(v ?? '').toLowerCase().includes(q))
    );
  }, [rows, search]);

  // Pagination over filtered rows
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage]);

  const exportCsv = useCallback(() => {
    const esc = (val: any) => {
      if (val === null || val === undefined) return '';
      const s = String(val);
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const header = columns.map((c) => toTitle(c)).join(',');
    // export all filtered rows (not just current page)
    const lines = filteredRows.map((r) => columns.map((c) => esc(r[c])).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fname = `donations_${entity}_${fromDate || 'all'}_${toDate || 'all'}.csv`;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredRows, columns, entity, fromDate, toDate]);

  const totalAmount = useMemo(() => filteredRows.reduce((sum, r) => sum + (Number(r.donationAmount) || 0), 0), [filteredRows]);

  // Styles for a DataTable-like experience
  const thStyle: React.CSSProperties = {
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    background: '#f8f9fa',
    zIndex: 1,
  };
  const baseTdStyle: React.CSSProperties = {
    whiteSpace: 'nowrap',
  };
  const getThStyle = (col: string): React.CSSProperties => {
    const st: React.CSSProperties = { ...thStyle };
    st.minWidth = getMinWidth(col);
    if (col === 'donationAmount') st.textAlign = 'right';
    else st.textAlign = 'left';
    return st;
  };
  const getTdStyle = (col: string): React.CSSProperties => {
    const st: React.CSSProperties = { ...baseTdStyle };
    st.minWidth = getMinWidth(col);
    if (col === 'donationAmount') st.textAlign = 'right';
    else st.textAlign = 'left';
    return st;
  };

  // Dynamic table min-width so columns have comfortable width without cramping
  const tableMinWidth = useMemo(() => {
    return columns.reduce((sum, c) => sum + getMinWidth(c), 0) + 100;
  }, [columns, getMinWidth]);

  return (
    <Container className="py-4">
      <Card className="mb-3 shadow-sm">
        <Card.Header>Donation Report</Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col sm={12} md={3}>
              <Form.Label htmlFor="entitySelect">Select Entity</Form.Label>
              <Form.Select
                id="entitySelect"
                value={entity}
                onChange={(e) => setEntity(e.target.value as 'RMS' | 'HYC')}
              >
                <option value="RMS">RMS</option>
                <option value="HYC">HYC</option>
              </Form.Select>
            </Col>
            <Col sm={12} md={3}>
              <Form.Label htmlFor="fromDate">From</Form.Label>
              <Form.Control
                id="fromDate"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Col>
            <Col sm={12} md={3}>
              <Form.Label htmlFor="toDate">To</Form.Label>
              <Form.Control
                id="toDate"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Col>
            <Col sm={12} md={3}>
              <Form.Label htmlFor="searchBox">Search</Form.Label>
              <Form.Control
                id="searchBox"
                type="text"
                placeholder="Type to filter results"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </Col>
          </Row>
          <Row className="g-2 align-items-center mt-2">
            <Col sm={12} md={6} className="d-flex" style={{ gap: 8 }}>
              <Button variant="primary" onClick={fetchData} disabled={loading} className="me-2">
                {loading ? (<><Spinner animation="border" size="sm" className="me-2" />Loading...</>) : 'Search'}
              </Button>
              <Button variant="success" onClick={exportCsv} disabled={filteredRows.length === 0}>Export to Excel</Button>
            </Col>
            <Col sm={12} md={6} className="text-md-end text-muted">
              Showing {(filteredRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1)}-
              {Math.min(currentPage * pageSize, filteredRows.length)} of {filteredRows.length} filtered rows
              {rows.length !== filteredRows.length ? ` (from ${rows.length} total)` : ''}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>Total Records: {filteredRows.length}{rows.length !== filteredRows.length ? ` (from ${rows.length})` : ''}</div>
            <div>Total Donation Amount: ${totalAmount.toFixed(2)}</div>
          </div>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <Table striped bordered hover size="sm" responsive className="align-middle" style={{ minWidth: tableMinWidth }}>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={String(col)} style={getThStyle(String(col))}>
                      {toTitle(String(col))}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center text-muted">No data</td>
                  </tr>
                ) : (
                  pagedRows.map((r, idx) => (
                    <tr key={idx}>
                      {columns.map((c) => (
                        <td key={String(c)} style={getTdStyle(String(c))}>
                          {String(r[c] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          <div className="d-flex justify-content-center">
            <Pagination className="mb-0">
              <Pagination.First disabled={currentPage === 1} onClick={() => setPage(1)} />
              <Pagination.Prev disabled={currentPage === 1} onClick={() => setPage(Math.max(1, currentPage - 1))} />
              {Array.from({ length: totalPages }).map((_, i) => {
                const p = i + 1;
                // Show only first, last, current, and neighbors for large page counts
                const show = p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2;
                if (!show) {
                  // Insert ellipsis at appropriate places
                  if (p === 2 && currentPage > 4) return <Pagination.Ellipsis key="start-ell" disabled />;
                  if (p === totalPages - 1 && currentPage < totalPages - 3) return <Pagination.Ellipsis key="end-ell" disabled />;
                  return null;
                }
                return (
                  <Pagination.Item key={p} active={p === currentPage} onClick={() => setPage(p)}>
                    {p}
                  </Pagination.Item>
                );
              })}
              <Pagination.Next disabled={currentPage === totalPages} onClick={() => setPage(Math.min(totalPages, currentPage + 1))} />
              <Pagination.Last disabled={currentPage === totalPages} onClick={() => setPage(totalPages)} />
            </Pagination>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default DonationReport;
